import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lightning, X } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { t } from "@/lib/i18n";

export const CalibrationModal = ({ onClose, onDone }) => {
  const [emails, setEmails] = useState([]);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/priority/calibrate/sample").then((r) => setEmails(r.data.emails || [])).catch((e) => toast.error(errDetail(e)));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const placements = Object.entries(choices).map(([thread_id, bucket]) => ({ thread_id, bucket }));
      const { data } = await api.post("/priority/calibrate", { placements });
      toast.success(t("pi.cal_done", "Calibrated — your starting weights are set"));
      onDone(data);
    } catch (e) { toast.error(errDetail(e)); setBusy(false); }
  };

  const Btn = ({ tid, bucket, label, color }) => (
    <button data-testid={`cal-${bucket}-${tid}`}
      onClick={() => setChoices({ ...choices, [tid]: bucket })}
      className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${choices[tid] === bucket ? color : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="calibration-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-heading font-bold text-gray-900 flex items-center gap-2"><Lightning size={18} className="text-amber-500" weight="fill" /> {t("pi.cal_title", "Quick calibration — sort 10 recent emails")}</h2>
          <button data-testid="cal-close-btn" onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{t("pi.cal_desc", "Tell me where each of these belongs. This sets your starting weights instantly — you can skip any you're unsure about.")}</p>
        {emails.map((e) => (
          <div key={e.thread_id} data-testid={`cal-row-${e.thread_id}`} className="flex items-center gap-3 border-b border-gray-100 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{e.from_name || e.from_email}</p>
              <p className="text-xs text-gray-500 truncate">{e.subject}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Btn tid={e.thread_id} bucket="now" label={t("pi.cal_now", "Act now")} color="border-red-300 bg-red-50 text-red-700" />
              <Btn tid={e.thread_id} bucket="later" label={t("pi.cal_later", "Later")} color="border-amber-300 bg-amber-50 text-amber-700" />
              <Btn tid={e.thread_id} bucket="ignore" label={t("pi.cal_ignore", "Ignore")} color="border-gray-400 bg-gray-100 text-gray-700" />
            </div>
          </div>
        ))}
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="cal-skip-btn" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800 px-3">{t("pi.cal_skip", "Skip for now")}</button>
          <button data-testid="cal-submit-btn" onClick={submit} disabled={busy || Object.keys(choices).length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2 disabled:opacity-50">
            {t("pi.cal_save", "Save my answers")} ({Object.keys(choices).length})
          </button>
        </div>
      </div>
    </div>
  );
};
